import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';
import { useTranslation } from 'react-i18next';
import { 
  Users, UserPlus, DollarSign, Activity, Search, Network,
  ChevronDown, ChevronRight, User, TrendingUp, Award, Filter
} from 'lucide-react';
import AnimatedCounter from '../../components/common/AnimatedCounter';
import { toast } from '../../components/common/Toast';
import { adminMlmAPI, getApiError } from '../../services/api';

const TreeNode = ({ node, depth = 0, isDarkMode }) => {
  const [expanded, setExpanded] = useState(depth < 2);
  const hasChildren = node.children && node.children.length > 0;

  return (
    <div className="select-none">
      <div
        onClick={() => hasChildren && setExpanded(!expanded)}
        className={`flex items-center gap-3 px-4 py-3 rounded-2xl cursor-pointer transition-all ${
          isDarkMode 
            ? 'hover:bg-gray-700/50 text-gray-300' 
            : 'hover:bg-orange-50 text-gray-700'
        } ${depth > 0 ? 'ml-8' : ''}`}
        style={{ paddingLeft: `${16 + depth * 24}px` }}
      >
        {hasChildren ? (
          expanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />
        ) : (
          <div className="w-4" />
        )}
        <div className={`w-8 h-8 rounded-xl flex items-center justify-center text-sm font-bold ${
          node.rank === 'Diamond' ? 'bg-gradient-to-br from-blue-500 to-cyan-500' :
          node.rank === 'Gold' ? 'bg-gradient-to-br from-yellow-400 to-orange-500' :
          node.rank === 'Silver' ? 'bg-gradient-to-br from-gray-300 to-gray-400' :
          'bg-gradient-to-br from-orange-500 to-orange-600'
        } text-white shadow-md`}>
          {node.name?.[0] || node.sttora_id?.[0] || '?'}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm truncate">{node.name || `ID: ${node.sttora_id || node.id}`}</span>
            {node.rank && (
              <span className={`px-2 py-0.5 rounded-lg text-[10px] font-medium ${
                node.rank === 'Diamond' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                node.rank === 'Gold' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
              }`}>{node.rank}</span>
            )}
          </div>
          <div className="flex items-center gap-3 text-xs text-gray-400">
            <span>{node.sttora_id || ''}</span>
            {node.volume !== undefined && <span>Vol: ${(node.volume || 0).toLocaleString()}</span>}
          </div>
        </div>
        {node.total_sales !== undefined && (
          <span className="text-sm font-semibold text-orange-600 dark:text-orange-400">${(node.total_sales || 0).toLocaleString()}</span>
        )}
      </div>
      <AnimatePresence>
        {expanded && hasChildren && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}>
            {node.children.map((child, i) => (
              <TreeNode key={child.id || i} node={child} depth={depth + 1} isDarkMode={isDarkMode} />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const NetworkView = () => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const [overview, setOverview] = useState(null);
  const [tree, setTree] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await adminMlmAPI.getNetworkOverview();
        const data = res.data || {};
        setOverview(data);
        setTree(data.tree || data.network || null);
      } catch (err) {
        toast.error(getApiError(err, 'Error al cargar red'));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const statsCards = [
    { label: 'Total Gestores', value: overview?.total_gestores || overview?.total_users || 0, icon: Users, color: 'from-blue-500 to-cyan-500' },
    { label: 'Gestores Activos', value: overview?.active_gestores || 0, icon: UserPlus, color: 'from-green-500 to-emerald-500' },
    { label: 'Volumen Total', value: overview?.total_volume || overview?.total_sales || 0, icon: DollarSign, color: 'from-orange-500 to-red-500' },
    { label: 'Rango Promedio', value: overview?.avg_rank || overview?.average_rank || 'N/A', icon: Award, color: 'from-purple-500 to-pink-500' },
  ];

  const filteredTree = (node) => {
    if (!node || !searchTerm) return node;
    const match = node.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  node.sttora_id?.toLowerCase().includes(searchTerm.toLowerCase());
    const filteredChildren = node.children ? node.children.map(filteredTree).filter(Boolean) : [];
    if (match || filteredChildren.length > 0) {
      return { ...node, children: filteredChildren };
    }
    return null;
  };

  const displayTree = tree ? filteredTree(tree) : null;

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-600" />
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
      <motion.div initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}>
        <h1 className={`text-3xl font-bold bg-gradient-to-r ${isDarkMode ? 'from-gray-100 to-gray-300' : 'from-gray-900 to-gray-600'} bg-clip-text text-transparent`}>
          {t('common.network')}
        </h1>
        <p className={`mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Visión general de la red de gestores</p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        {statsCards.map((item, i) => (
          <StatCard key={item.label} item={item} index={i} isDarkMode={isDarkMode} />
        ))}
      </div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className={`${isDarkMode ? 'bg-gray-800/80' : 'bg-white/80'} backdrop-blur-sm rounded-3xl border ${isDarkMode ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-sm overflow-hidden`}
      >
        <div className="p-6 border-b border-gray-100 dark:border-gray-700/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-gradient-to-r from-orange-500 to-pink-500 shadow-lg">
                <Network className="w-5 h-5 text-white" />
              </div>
              <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Árbol de la Red</h3>
            </div>
            <div className="relative max-w-xs w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input type="text" placeholder="Buscar por nombre o ID..."
                value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                className={`w-full pl-10 pr-4 py-2.5 rounded-2xl border text-sm ${
                  isDarkMode ? 'bg-gray-700/50 border-gray-600 text-white placeholder-gray-400' : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-500'
                } focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition-all`}
              />
            </div>
          </div>
        </div>
        <div className="p-2">
          {displayTree ? (
            <TreeNode node={displayTree} depth={0} isDarkMode={isDarkMode} />
          ) : (
            <div className="text-center py-12">
              <Network className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Red no disponible</h3>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No se encontraron datos de la red de gestores</p>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

const StatCard = ({ item, index, isDarkMode }) => (
  <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
    transition={{ delay: index * 0.1 }} whileHover={{ y: -4 }}
    className={`${isDarkMode ? 'bg-gray-800/80' : 'bg-white/80'} backdrop-blur-sm rounded-3xl p-6 border ${isDarkMode ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-sm hover:shadow-xl transition-all`}
  >
    <div className="flex items-center justify-between">
      <div>
        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{item.label}</p>
        <p className={`text-3xl font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          {typeof item.value === 'number' ? <AnimatedCounter value={item.value} /> : item.value}
        </p>
      </div>
      <div className={`p-3 rounded-2xl bg-gradient-to-r ${item.color} shadow-lg`}>
        <item.icon className="w-5 h-5 text-white" />
      </div>
    </div>
  </motion.div>
);

export default NetworkView;
